<template>
  <div class="empresa-cnpj-select-wrapper">
    <b-form-group :label="label">
      <validation-provider
        #default="{ errors }"
        :rules="{ required: requiredRule }"
        name="Empresa"
      >
        <div class="empresa-input-container">
          <div class="empresa-field">
            <v-select
              ref="empresaSelect"
              v-model="selecionado"
              :options="empresasFiltradas"
              :clearable="false"
              :disabled="disabled"
              :filter="() => true"
              :placeholder="placeholderBusca"
              label="razaoSocial"
              @open="carregarEmpresas"
              @search="onSearch"
              @search:focus="handleSearchFocus"
              @search:blur="handleSearchBlur"
              @input="handleSelect"
            >
              <template #selected-option="{ cadastroFederal, razaoSocial }">
                <span>{{ cadastroFederal }} - {{ razaoSocial }}</span>
              </template>
              <template #option="option">
                <div class="company-identification d-flex align-items-center">
                  <span>{{ option.cadastroFederal }}</span>
                  <span
                    v-if="option.isMatriz !== undefined"
                    class="ml-50 text-primary text-capitalize"
                  >
                    {{ option.isMatriz ? 'Matriz' : 'Filial' }}
                  </span>
                </div>
                {{ option.razaoSocial }}
              </template>
              <template #no-options="{ search }">
                <div v-if="cnpjCompleto(search)">
                  Digite o CNPJ completo e clique em buscar.
                </div>
                <div v-else>
                  Nenhuma empresa encontrada.
                </div>
              </template>
            </v-select>
          </div>

          <b-button
            variant="primary"
            size="sm"
            class="btn-buscar-cnpj"
            :disabled="!botaoBuscarHabilitado || buscando"
            @click.stop="buscarCnpj"
          >
            <b-spinner
              v-if="buscando"
              small
            />
            <feather-icon
              v-else
              icon="SearchIcon"
            />
          </b-button>

          <b-button
            v-if="exibirBotaoCriarPessoa && selecionado"
            v-b-tooltip.hover.top="'Criar pessoa responsável'"
            size="sm"
            variant="none"
            class="btn-add-pessoa p-0"
            @click="$emit('abrir-modal-pessoa', selecionado)"
          >
            <font-awesome-icon
              :icon="faPlus"
              class="text-primary"
            />
          </b-button>
        </div>
        <small
          v-if="mensagem"
          :class="mensagemClasse"
        >{{ mensagem }}</small>
        <small class="text-danger">{{ errors[0] }}</small>
      </validation-provider>
    </b-form-group>

    <b-modal
      v-model="showModalConfirmarCriacao"
      title="Confirmar dados da empresa"
      ok-title="Criar empresa"
      cancel-title="Cancelar"
      centered
      @ok="confirmarCriacaoCnpja"
      @cancel="cancelarCriacaoCnpja"
    >
      <b-alert
        variant="success"
        show
        class="mb-2"
      >
        <feather-icon
          icon="CheckCircleIcon"
          class="mr-50"
        />
        Dados encontrados na Receita Federal.
      </b-alert>
      <dl class="row mb-1">
        <dt class="col-sm-4">
          CNPJ:
        </dt>
        <dd class="col-sm-8">
          {{ (dadosCnpja && dadosCnpja.cnpj) || texto }}
        </dd>
        <dt class="col-sm-4">
          Razão Social:
        </dt>
        <dd class="col-sm-8">
          {{ (dadosCnpja && dadosCnpja.razaoSocial) || '-' }}
        </dd>
        <dt class="col-sm-4">
          Nome Fantasia:
        </dt>
        <dd class="col-sm-8">
          {{ (dadosCnpja && dadosCnpja.nomeFantasia) || '-' }}
        </dd>
      </dl>
      <p class="mb-0">
        Deseja cadastrar esta empresa{{ pessoaNome ? ` e vinculá-la a ${pessoaNome}` : '' }}?
      </p>
    </b-modal>

    <b-modal
      v-model="showModalCriacaoManual"
      title="Criar empresa manualmente"
      ok-title="Criar empresa"
      cancel-title="Cancelar"
      centered
      :ok-disabled="razaoSocialManual.trim() === ''"
      @ok="confirmarCriacaoManual"
      @cancel="cancelarCriacaoManual"
    >
      <b-alert
        variant="warning"
        show
        class="mb-2"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          class="mr-50"
        />
        {{ motivoFallback }}
      </b-alert>
      <b-form-group label="CNPJ">
        <b-form-input
          v-model="texto"
          readonly
        />
      </b-form-group>
      <b-form-group label="Razão Social *">
        <b-form-input
          v-model="razaoSocialManual"
          placeholder="Digite a razão social"
          :state="razaoSocialManual.trim() !== '' ? true : null"
          autofocus
        />
        <b-form-invalid-feedback :state="razaoSocialManual.trim() !== ''">
          Informe a razão social da empresa.
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group label="Nome Fantasia">
        <b-form-input
          v-model="nomeFantasiaManual"
          placeholder="Digite o nome fantasia (opcional)"
        />
      </b-form-group>
    </b-modal>

    <modal-criar-pessoa
      ref="modalCriarPessoa"
      :empresa-id="empresaCriadaId"
      :dados-iniciais="dadosSocio"
      @pessoa-criada="handlePessoaCriada"
    />
  </div>
</template>

<script>
import {
  BAlert,
  BButton,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BModal,
  BSpinner,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { ValidationProvider } from 'vee-validate'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import ModalCriarPessoa from '@/app-modulo/comercial/cadastro-proposta/shared/components/modal-criar-pessoa/ModalCriarPessoa.vue'
import empresaApi from '@/app-modulo/comuns/empresa/shared/services/api'
import pessoaApi from '@/app-modulo/comercial/pessoa/shared/services/api'
import { handlePropostaError } from '@/app-modulo/comercial/cadastro-proposta/shared/utils/errorHandler'
import '@core/scss/vue/libs/vue-select.scss'

export default {
  name: 'EmpresaCnpjSelect',
  components: {
    BAlert,
    BButton,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BModal,
    BSpinner,
    ValidationProvider,
    vSelect,
    ModalCriarPessoa,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    pessoa: {
      type: Object,
      default: null,
    },
    label: {
      type: String,
      default: 'Empresa',
    },
    placeholderBusca: {
      type: String,
      default: 'Digite o CNPJ ou o nome da empresa',
    },
    exibirBotaoCriarPessoa: {
      type: Boolean,
      default: false,
    },
    require: {
      type: Boolean,
      default: false,
    },
    filtros: {
      type: Array,
      default: () => ([
        {
          Colunas: ['Ativo'],
          Valores: [true],
        },
      ]),
    },
  },
  data() {
    return {
      faPlus,
      texto: '',
      empresas: [],
      empresasFiltradas: [],
      selecionado: this.value,
      primeiroAcesso: true,
      buscando: false,
      mensagem: '',
      mensagemTipo: '',
      dadosCnpja: null,
      showModalConfirmarCriacao: false,
      showModalCriacaoManual: false,
      motivoFallback: '',
      razaoSocialManual: '',
      nomeFantasiaManual: '',
      empresaCriadaId: null,
      dadosSocio: {},
      searchFocused: false,
    }
  },
  computed: {
    requiredRule() {
      return this.require
    },
    botaoBuscarHabilitado() {
      const cnpjLimpo = this.texto.replace(/\D/g, '')
      return cnpjLimpo.length === 14 && this.validarDigitosCnpj(cnpjLimpo)
    },
    mensagemClasse() {
      if (this.mensagemTipo === 'success') return 'text-success'
      if (this.mensagemTipo === 'warning') return 'text-warning'
      return 'text-danger'
    },
    pessoaNome() {
      return (this.pessoa && this.pessoa.nome) || ''
    },
  },
  watch: {
    value(val) {
      this.selecionado = val
    },
    filtros: {
      deep: true,
      handler() {
        this.primeiroAcesso = true
        this.carregarEmpresas()
      },
    },
  },
  created() {
    this.carregarEmpresas()
  },
  methods: {
    onSearch(valor) {
      if (typeof valor !== 'string') return
      if (valor === '' && this.texto && !this.searchFocused) {
        // blur do vue-select limpou o campo, restaura o que o usuário digitou
        this.$nextTick(() => {
          if (this.$refs.empresaSelect) {
            this.$refs.empresaSelect.search = this.texto
          }
        })
        return
      }
      this.texto = valor
      // eslint-disable-next-line no-console
      console.log('[EmpresaCnpjSelect] onSearch texto:', this.texto)
      this.filtrarEmpresas(this.texto)
      this.limparMensagem()
    },
    handleSearchFocus() {
      this.searchFocused = true
    },
    handleSearchBlur() {
      this.searchFocused = false
    },
    filtrarEmpresas(termo) {
      if (!termo || termo.trim() === '') {
        this.empresasFiltradas = [...this.empresas]
        return
      }
      const query = termo.toLowerCase()
      this.empresasFiltradas = this.empresas.filter(empresa => {
        const nome = (empresa.razaoSocial && empresa.razaoSocial.toLowerCase()) || ''
        const fantasia = (empresa.nomeFantasia && empresa.nomeFantasia.toLowerCase()) || ''
        const doc = empresa.cadastroFederal || ''
        return nome.includes(query) || fantasia.includes(query) || doc.includes(termo)
      })
    },
    carregarEmpresas() {
      if (!this.primeiroAcesso) {
        this.empresasFiltradas = [...this.empresas]
        return
      }
      const filtrosRequest = Array.isArray(this.filtros) && this.filtros.length > 0 ? this.filtros : [{
        Colunas: ['Ativo'],
        Valores: [true],
      }]
      empresaApi.getEmpresasResumidas(filtrosRequest)
        .then(payload => {
          this.empresas = payload.data || []
          this.empresasFiltradas = [...this.empresas]
          this.primeiroAcesso = false
        })
        .catch(() => {
          if (this.$erro) {
            this.$erro('Erro ao carregar empresas')
          }
        })
    },
    handleSelect(empresa) {
      this.selecionado = empresa
      this.$emit('input', empresa || null)
      this.$emit('select', empresa || null)
      this.$emit('empresa-selecionada', empresa || null)
      this.limparMensagem()
      if (this.$refs.empresaSelect) {
        this.$refs.empresaSelect.search = ''
      }
      this.texto = ''
      this.filtrarEmpresas('')
    },
    async buscarCnpj() {
      const searchValue = (this.$refs.empresaSelect && this.$refs.empresaSelect.search) || this.texto
      if (searchValue && searchValue !== this.texto) {
        this.texto = searchValue
      }
      // eslint-disable-next-line no-console
      console.log('[EmpresaCnpjSelect] buscarCnpj acionado', this.texto)
      if (!this.botaoBuscarHabilitado) {
        this.setMensagem('Informe um CNPJ válido para buscar.', 'danger')
        return
      }
      this.buscando = true
      this.limparMensagem()
      try {
        const empresaLocal = await this.buscarEmpresaLocal()
        if (empresaLocal) {
          this.setMensagem('Empresa encontrada no sistema!', 'success')
          this.selecionado = empresaLocal
          this.$emit('input', empresaLocal)
          this.$emit('empresa-selecionada', empresaLocal)
        } else {
          await this.buscarNoCnpja()
        }
      } catch (error) {
        const { message } = handlePropostaError(error)
        this.setMensagem(message, 'danger')
      } finally {
        this.buscando = false
      }
    },
    async buscarEmpresaLocal() {
      const cnpjLimpo = this.texto.replace(/\D/g, '')
      // eslint-disable-next-line no-console
      console.log('[EmpresaCnpjSelect] buscarEmpresaLocal', cnpjLimpo)
      const existeResponse = await empresaApi.existeInscricao(cnpjLimpo, 0)
      if (existeResponse.data === true) {
        const response = await empresaApi.getEmpresasResumidas([{
          Colunas: ['CadastroFederal'],
          Valores: [cnpjLimpo],
        }])
        if (response.data && response.data.length > 0) {
          const lista = response.data
          const match = lista.find(e => String(e.cadastroFederal || '').replace(/\D/g, '') === cnpjLimpo)
          return match || lista[0]
        }
      }
      return null
    },
    async buscarNoCnpja() {
      try {
        const cnpjLimpo = this.texto.replace(/\D/g, '')
        // eslint-disable-next-line no-console
        console.log('[EmpresaCnpjSelect] buscarNoCnpja', cnpjLimpo)
        const response = await empresaApi.getInformacaoEmpresa(cnpjLimpo)
        if (response.data) {
          this.dadosCnpja = response.data
          this.showModalConfirmarCriacao = true
        } else {
          this.motivoFallback = 'CNPJ não encontrado na Receita Federal.'
          this.showModalCriacaoManual = true
        }
      } catch {
        this.motivoFallback = 'Não foi possível consultar a Receita Federal no momento.'
        this.showModalCriacaoManual = true
      }
    },
    async confirmarCriacaoCnpja() {
      try {
        this.buscando = true
        const novaEmpresa = await this.criarEmpresaComDados({
          cadastroFederal: this.texto.replace(/\D/g, ''),
          razaoSocial: (this.dadosCnpja && this.dadosCnpja.razaoSocial) || '',
          nomeFantasia: (this.dadosCnpja && this.dadosCnpja.nomeFantasia) || '',
        })
        await this.vincularEmpresaPessoa(novaEmpresa)
        this.$emit('empresa-criada', novaEmpresa)
        this.setMensagem('Empresa criada com sucesso!', 'success')
      } catch (error) {
        const { message } = handlePropostaError(error)
        this.setMensagem(message, 'danger')
      } finally {
        this.buscando = false
        this.showModalConfirmarCriacao = false
      }
    },
    async confirmarCriacaoManual() {
      if (this.razaoSocialManual.trim() === '') {
        this.setMensagem('Informe a razão social da empresa.', 'danger')
        return
      }
      try {
        this.buscando = true
        const novaEmpresa = await this.criarEmpresaComDados({
          cadastroFederal: this.texto.replace(/\D/g, ''),
          razaoSocial: this.razaoSocialManual.trim(),
          nomeFantasia: this.nomeFantasiaManual.trim(),
        })
        await this.vincularEmpresaPessoa(novaEmpresa)
        this.$emit('empresa-criada', novaEmpresa)
        this.setMensagem('Empresa criada manualmente com sucesso!', 'success')
      } catch (error) {
        const { message } = handlePropostaError(error)
        this.setMensagem(message, 'danger')
      } finally {
        this.buscando = false
        this.showModalCriacaoManual = false
        this.razaoSocialManual = ''
        this.nomeFantasiaManual = ''
      }
    },
    async criarEmpresaComDados({ cadastroFederal, razaoSocial, nomeFantasia }) {
      const timestamp = Date.now()
      const cnpjSufixo = cadastroFederal.substring(0, 8)
      const emailPessoa = (this.pessoa && this.pessoa.email) || `empresa${cnpjSufixo}@temp-${timestamp}.com.br`
      const empresaData = {
        tipoInscricao: 'CNPJ',
        cadastroFederal,
        razaoSocial: razaoSocial || `Empresa CNPJ ${this.texto}`,
        nomeFantasia: nomeFantasia || '',
        emailsBoletosNFs: [emailPessoa.toLowerCase()],
        ativo: true,
        clienteFixo: false,
        departamentos: [],
        ieItens: [],
        imItens: [],
        cnaeSecundarios: [],
        servicos: [],
        inscricoesEstaduais: [],
        inscricoesMunicipais: [],
      }
      await empresaApi.createEmpresa(empresaData)
      const empresasResponse = await empresaApi.getEmpresasResumidas([{
        Colunas: ['CadastroFederal'],
        Valores: [cadastroFederal],
      }])
      if (empresasResponse.data && empresasResponse.data.length > 0) {
        const lista = empresasResponse.data
        const empresaCriada = lista.find(e => String(e.cadastroFederal || '').replace(/\D/g, '') === cadastroFederal) || lista[0]
        this.empresas.push(empresaCriada)
        this.empresasFiltradas = [...this.empresas]
        this.selecionado = empresaCriada
        this.$emit('input', empresaCriada)
        this.$emit('empresa-selecionada', empresaCriada)
        this.texto = ''
        return empresaCriada
      }
      throw new Error('Empresa criada, mas não foi possível recuperá-la.')
    },
    cancelarCriacaoCnpja() {
      this.showModalConfirmarCriacao = false
      this.dadosCnpja = null
    },
    async vincularEmpresaPessoa(empresa) {
      if (!empresa || !(this.pessoa && this.pessoa.id)) return
      try {
        const response = await pessoaApi.getPessoa(this.pessoa.id)
        const pessoa = response.data
        pessoa.empresas = pessoa.empresas || []
        const empresaJaVinculada = pessoa.empresas.some(e => e.id === empresa.id)
        if (empresaJaVinculada) {
          return
        }
        pessoa.empresas.push(empresa)
        await pessoaApi.updatePessoa(this.pessoa.id, pessoa)
      } catch {
        if (this.$erro) {
          this.$erro('Erro ao vincular empresa ao contato.')
        }
      }
    },
    cancelarCriacaoManual() {
      this.showModalCriacaoManual = false
      this.razaoSocialManual = ''
      this.nomeFantasiaManual = ''
      this.motivoFallback = ''
    },
    validarDigitosCnpj(cnpj) {
      if (/^(\d)\1{13}$/.test(cnpj)) return false
      let tamanho = cnpj.length - 2
      let numeros = cnpj.substring(0, tamanho)
      const digitos = cnpj.substring(tamanho)
      let soma = 0
      let pos = tamanho - 7
      for (let i = tamanho; i >= 1; i -= 1) {
        soma += numeros.charAt(tamanho - i) * pos
        pos -= 1
        if (pos < 2) pos = 9
      }
      let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
      if (resultado !== parseInt(digitos.charAt(0), 10)) return false
      tamanho += 1
      numeros = cnpj.substring(0, tamanho)
      soma = 0
      pos = tamanho - 7
      for (let i = tamanho; i >= 1; i -= 1) {
        soma += numeros.charAt(tamanho - i) * pos
        pos -= 1
        if (pos < 2) pos = 9
      }
      resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11)
      return resultado === parseInt(digitos.charAt(1), 10)
    },
    cnpjCompleto(texto) {
      const digitos = (texto || '').replace(/\D/g, '')
      return digitos.length === 14
    },
    setMensagem(texto, tipo) {
      this.mensagem = texto
      this.mensagemTipo = tipo
    },
    limparMensagem() {
      this.mensagem = ''
      this.mensagemTipo = ''
    },
    validateSelecao() {
      if (this.selecionado) {
        return true
      }
      const cnpjDigitado = this.texto.replace(/\D/g, '')
      if (cnpjDigitado.length === 14) {
        this.setMensagem('Clique em buscar ou selecione uma empresa na lista.', 'danger')
        return false
      }
      return true
    },
    resetarEstado() {
      this.texto = ''
      this.limparMensagem()
      this.showModalConfirmarCriacao = false
      this.showModalCriacaoManual = false
      this.dadosCnpja = null
      this.razaoSocialManual = ''
      this.nomeFantasiaManual = ''
    },
    async processarSociosDaEmpresa(empresaId) {
      if (this.dadosCnpja && this.dadosCnpja.socios && this.dadosCnpja.socios.length > 0) {
        const primeiroSocio = this.dadosCnpja.socios[0]
        const pessoaCriada = await this.tentarCriarPessoaDoSocio(primeiroSocio, empresaId)
        if (!pessoaCriada) {
          this.abrirModalCriarPessoa(primeiroSocio, empresaId)
        }
      } else {
        this.abrirModalCriarPessoa({}, empresaId)
      }
    },
    async tentarCriarPessoaDoSocio(socio, empresaId) {
      try {
        if (!socio.nome || !socio.email) {
          return null
        }
        const pessoaData = {
          nome: socio.nome,
          email: socio.email,
          comoChegou: 'Outros',
          complementoOutros: 'Criado automaticamente via CNPJá',
          empresas: [{ id: empresaId }],
          ativo: true,
        }
        const response = await pessoaApi.createPessoa(pessoaData)
        if (response.data) {
          this.$emit('pessoa-criada', response.data)
          if (this.$sucesso) {
            this.$sucesso('Pessoa criada automaticamente a partir do sócio!')
          }
          return response.data
        }
        return null
      } catch {
        return null
      }
    },
    abrirModalCriarPessoa(socio, empresaId) {
      this.dadosSocio = {
        nome: socio.nome || '',
        email: socio.email || '',
      }
      this.empresaCriadaId = empresaId
      if (this.$refs.modalCriarPessoa && this.$refs.modalCriarPessoa.show) {
        this.$refs.modalCriarPessoa.show()
      }
    },
    handlePessoaCriada(pessoaData) {
      this.$emit('pessoa-criada', pessoaData)
    },
  },
}
</script>

<style lang="scss" scoped>
.empresa-cnpj-select-wrapper {
  .empresa-input-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    position: relative;

    .empresa-field {
      flex: 1;
    }
  }

  .btn-buscar-cnpj {
    padding: 0.35rem 0.55rem;
  }

  .btn-add-pessoa {
    position: absolute;
    top: 0;
    right: 0.5rem;
    width: 24px;
    height: 24px;
    z-index: 3;
  }

  .company-identification span {
    font-size: 0.65rem;
    color: var(--primary);
    font-weight: 700;
    border: 1px solid var(--primary) !important;
    border-radius: 2px;
    padding: 0.1rem 0.2rem;
  }

  .vs__actions {
    display: flex;
    align-items: center;

    .btn {
      margin-left: 0.25rem;
      padding: 0.25rem 0.4rem;
    }
  }
}
</style>
